import PageBanner from "../components/PageBanner";
import { CheckCircle2, Award, Factory, Users, Target, Eye, Heart } from "lucide-react";
import { company, stats, clients } from "../mock/mock";
import { Link } from "react-router-dom";

export default function About() {
  return (
    <div>
      <PageBanner
        title="About Us"
        subtitle="Premium adhesive tape solutions backed by decades of expertise"
        breadcrumbs={[{ label: "About Us" }]}
      />

      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[12px] uppercase tracking-[0.3em] text-[#c8102e] font-semibold mb-3">Who We Are</p>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#0d2240] section-title">
              {company.name}
            </h2>
            <p className="text-gray-600 mt-6 leading-relaxed">
              {company.name} is a Vadodara based manufacturer, supplier, wholesaler, trader and retailer of
              premium adhesive tapes. Established in 2008, we have grown into a trusted name supplying
              speciality industrial tapes to clients across Gujarat, Maharashtra, Delhi, Karnataka, Tamil
              Nadu and beyond.
            </p>
            <p className="text-gray-600 mt-3 leading-relaxed">
              Our portfolio spans <strong className="text-[#0d2240]">15 product ranges</strong> with over
              <strong className="text-[#0d2240]"> 75+ tape variants</strong> covering BOPP, masking, double sided,
              surface protection, aluminium foil, duct, electrical, foam, kraft, filament, VHB and printed tapes.
            </p>
            <ul className="grid grid-cols-2 gap-3 mt-6">
              {[
                "100% Quality Tested",
                "Custom Slitting & Conversion",
                "Bulk Wholesale Supply",
                "OEM & B2B Partnerships",
                "Eco-friendly Options",
                "Pan-India Distribution",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-[#0d2240]">
                  <CheckCircle2 size={18} className="text-[#c8102e] shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <img src="https://images.unsplash.com/photo-1773525912464-d2640e7aff9c?auto=format&fit=crop&w=600&q=70" className="rounded-lg w-full h-56 object-cover" alt="Factory" />
            <img src="https://images.unsplash.com/photo-1581091212991-8891c7d4bd9b?auto=format&fit=crop&w=600&q=70" className="rounded-lg w-full h-56 object-cover mt-8" alt="Team" />
            <img src="https://images.pexels.com/photos/4820844/pexels-photo-4820844.jpeg?auto=compress&w=600&q=70" className="rounded-lg w-full h-56 object-cover -mt-2" alt="Packaging" />
            <img src="https://images.unsplash.com/photo-1697982947220-01b97bdec381?auto=format&fit=crop&w=600&q=70" className="rounded-lg w-full h-56 object-cover mt-6" alt="Tapes" />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#c8102e]">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/15">
          {stats.map((s, i) => (
            <div key={i} className="py-7 px-4 text-center text-white">
              <div className="font-heading font-extrabold text-2xl md:text-3xl">{s.value}</div>
              <div className="text-[11.5px] md:text-xs uppercase tracking-widest text-white/80 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission Vision Values */}
      <section className="py-16 bg-[#f7f7f9]">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-3 gap-5">
          {[
            { Icon: Target, title: "Our Mission", desc: "To provide consistent, premium adhesive tape solutions that empower our customers' productivity, packaging and protection needs across India." },
            { Icon: Eye, title: "Our Vision", desc: "To be India’s most trusted partner for specialty adhesive tapes through quality, innovation and customer-first service." },
            { Icon: Heart, title: "Our Values", desc: "Integrity, quality, customer care, continuous improvement and timely delivery are the values that guide every decision we make." },
          ].map(({ Icon, title, desc }) => (
            <div key={title} className="card-lift bg-white rounded-lg p-7 border border-gray-100">
              <div className="w-12 h-12 rounded-md grid place-items-center bg-[#c8102e]/10 text-[#c8102e] mb-4">
                <Icon size={22} />
              </div>
              <h4 className="font-heading font-semibold text-[#0d2240] text-lg mb-2">{title}</h4>
              <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Clients */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <p className="text-[12px] uppercase tracking-[0.3em] text-[#c8102e] font-semibold mb-2">Our Clients</p>
            <h3 className="font-heading font-extrabold text-2xl md:text-3xl text-[#0d2240]">Trusted by Leading Brands</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {clients.map((c) => (
              <div key={c} className="h-20 grid place-items-center rounded-lg border border-gray-100 bg-[#f7f7f9] hover:bg-white hover:border-[#c8102e]/30 transition-colors text-center px-2">
                <span className="font-heading font-semibold text-[#0d2240] text-[13px]">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="rounded-xl bg-[#0d2240] text-white p-8 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h4 className="font-heading font-extrabold text-xl">Looking to partner with us?</h4>
              <p className="text-white/75 text-sm mt-1">Reach out for distributorship & bulk inquiries.</p>
            </div>
            <Link to="/contact-us" className="px-5 py-3 rounded-md bg-[#c8102e] hover:bg-[#9a0c24] text-white font-semibold text-sm">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
